import { apiClient } from './apiClient';
import { roleServiceMock } from './roleService.mock';
import type {
  Role,
  RoleListItem,
  CreateRoleDto,
  UpdateRoleDto,
  RoleRate,
  RoleRateHistory,
  CreateRoleRateDto,
  RoleAuditLog,
  AuditLogQuery,
} from '@/types/role';
import type { DataMode } from '@/contexts/DataSourceContext';

const realRoleService = {
  // Role CRUD operations
  async getAllRoles(): Promise<RoleListItem[]> {
    return await apiClient.get<RoleListItem[]>(`/roles`);
  },

  async getActiveRoles(): Promise<RoleListItem[]> {
    return await apiClient.get<RoleListItem[]>(`/roles/active`);
  },

  async getRoleById(id: number): Promise<Role> {
    return await apiClient.get<Role>(`/roles/${id}`);
  },

  async createRole(dto: CreateRoleDto, employeeId: number): Promise<Role> {
    return await apiClient.post<Role>(`/roles?employeeId=${employeeId}`, dto);
  },

  async updateRole(id: number, dto: UpdateRoleDto): Promise<Role> {
    return await apiClient.put<Role>(`/roles/${id}`, dto);
  },

  async archiveRole(id: number): Promise<void> {
    await apiClient.post(`/roles/${id}/archive`, {});
  },

  async unarchiveRole(id: number): Promise<void> {
    await apiClient.post(`/roles/${id}/unarchive`, {});
  },

  // Rate operations
  async getRateHistory(roleId: number): Promise<RoleRateHistory[]> {
    return await apiClient.get<RoleRateHistory[]>(`/roles/${roleId}/rates/history`);
  },

  async createRate(roleId: number, dto: CreateRoleRateDto): Promise<RoleRate> {
    return await apiClient.post<RoleRate>(`/roles/${roleId}/rates`, dto);
  },

  // Audit operations
  async getAuditLogs(query: AuditLogQuery): Promise<RoleAuditLog[]> {
    const params = new URLSearchParams();
    if (query.entityType) params.append('entityType', query.entityType);
    if (query.entityId) params.append('entityId', query.entityId.toString());
    if (query.startDate) params.append('startDate', query.startDate);
    if (query.endDate) params.append('endDate', query.endDate);
    if (query.changedBy) params.append('changedBy', query.changedBy);
    if (query.pageNumber) params.append('pageNumber', query.pageNumber.toString());
    if (query.pageSize) params.append('pageSize', query.pageSize.toString());

    return await apiClient.get<RoleAuditLog[]>(`/roles/audit-logs?${params.toString()}`);
  },
};

// Get the appropriate role service based on the data mode
export const getRoleService = (mode: DataMode) => {
  return mode === 'live' ? realRoleService : roleServiceMock;
};

// Default export for backward compatibility (uses mock by default)
export const roleService = roleServiceMock;
